import {
  Reducer,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';

import isEqual from 'lodash-es/isEqual';

export type FBQueryOptions<TResult> = {
  /** Called before request is fired off */
  onStart?: () => void;
  /** Called when request finishes successfully */
  onSuccess?: (result: TResult) => void;
  /** Called when an error occurs */
  onError?: (error: Error) => void;
  /** Called when request finishes, regardless of success or failure */
  onFinished?: () => void;
  /** When false, the query will not be run */
  enabled?: boolean;
};

export type FBMutationFunction<TResult, TArgs> = (
  queryArgs: TArgs
) => Promise<TResult>;

export interface FBQueryIdleStatus {
  status: 'idle';
  data: undefined;
  error: undefined;
  isError: false;
  isIdle: true;
  isPending: false;
  isSuccess: false;
}

export interface FBQuerySuccessStatus<TResult> {
  status: 'success';
  data: TResult;
  error: undefined;
  isError: false;
  isIdle: true;
  isPending: false;
  isSuccess: true;
}

export interface FBQueryErrorStatus {
  status: 'error';
  data: undefined;
  error: Error;
  isError: true;
  isIdle: true;
  isPending: false;
  isSuccess: false;
}

export interface FBQueryPendingStatus {
  status: 'pending';
  data: undefined;
  error: undefined;
  isError: false;
  isIdle: false;
  isPending: true;
  isSuccess: false;
}

export type FBQueryMutationStatus<TResult> =
  | FBQuerySuccessStatus<TResult>
  | FBQueryIdleStatus
  | FBQueryPendingStatus
  | FBQueryErrorStatus;

export type FBMutationHookResult<TResult, TArgs> =
  FBQueryMutationStatus<TResult> & {
    mutate: (queryArgs: TArgs) => void;
    reset: () => void;
  };

export type FBQueryHookResult<TResult> = FBQueryMutationStatus<TResult> & {
  reload: () => void;
};

function successStatus<TResult>(data: TResult): FBQuerySuccessStatus<TResult> {
  return {
    status: 'success',
    data,
    error: undefined,
    isIdle: true,
    isError: false,
    isPending: false,
    isSuccess: true,
  };
}

function errorStatus(err: Error): FBQueryErrorStatus {
  return {
    status: 'error',
    data: undefined,
    error: err,
    isIdle: true,
    isError: true,
    isPending: false,
    isSuccess: false,
  };
}

function pendingStatus(): FBQueryPendingStatus {
  return {
    status: 'pending',
    data: undefined,
    error: undefined,
    isSuccess: false,
    isPending: true,
    isError: false,
    isIdle: false,
  };
}

function idleStatus(): Omit<FBQueryIdleStatus, 'mutate' | 'reset'> {
  return {
    status: 'idle',
    data: undefined,
    error: undefined,
    isIdle: true,
    isError: false,
    isPending: false,
    isSuccess: false,
  };
}

export function useMutation<TResult, TArgs>(
  mutationFn: FBMutationFunction<TResult, TArgs>,
  options?: FBQueryOptions<TResult>
): FBMutationHookResult<TResult, TArgs> {
  const [status, setStatus] =
    useState<FBQueryMutationStatus<TResult>>(idleStatus);

  // We keep a ref to the current set of callbacks being passed into the hook
  // in order to avoid capturing the original callbacks in the closure. This
  // allows callbacks to refer to values from other hooks and behave the way
  // we'd expect them to.
  const callbackRef = useRef(options);

  useEffect(() => {
    callbackRef.current = options;
  }, [options]);

  const callback = useCallback(
    async (queryArgs: TArgs) => {
      try {
        if (callbackRef.current?.onStart) {
          callbackRef.current?.onStart();
        }
        setStatus(pendingStatus());
        const result = await mutationFn(queryArgs);
        if (callbackRef.current?.onSuccess) {
          callbackRef.current?.onSuccess(result);
        }
        setStatus(successStatus(result));
      } catch (e) {
        if (callbackRef.current?.onError) {
          callbackRef.current?.onError(e);
        }
        setStatus(errorStatus(e));
      } finally {
        if (callbackRef.current?.onFinished) {
          callbackRef.current?.onFinished();
        }
      }
    },
    [mutationFn, callbackRef]
  );
  return { ...status, mutate: callback, reset: () => setStatus(idleStatus()) };
}

export type FBQueryFunction<TArgs, TResult> = (args: TArgs) => Promise<TResult>;

export type FBQueryResult<TResult> = FBQueryMutationStatus<TResult> & {
  reload: () => void;
};

export type FBQueryQueryStatus<TResult> =
  | FBQuerySuccessStatus<TResult>
  | FBQueryPendingStatus
  | FBQueryErrorStatus;

type UseQueryAction<TResult> = {
  type: 'setStatus';
  status: FBQueryQueryStatus<TResult>;
};

function reducer<TResult>(
  state: FBQueryMutationStatus<TResult>,
  action: UseQueryAction<TResult>
): FBQueryMutationStatus<TResult> {
  if (action.type === 'setStatus') {
    return action.status;
  }
  return state;
}

export function useQuery<TArgs, TResult>(
  queryFn: FBQueryFunction<TArgs, TResult>,
  queryArgs: TArgs,
  options?: FBQueryOptions<TResult>
): FBQueryHookResult<TResult> {
  // Using useReducer as a glorified useState because it's less prone to
  // causing rerenders or stale closures.
  const [status, statusDispatch] = useReducer<
    Reducer<FBQueryMutationStatus<TResult>, UseQueryAction<TResult>>
  >(reducer, pendingStatus() as FBQueryResult<TResult>);

  // Hold onto the previously provided queryArgs, allowing us to perform our
  // own equality checking to determine if they've really changed.
  const lastArgsRef = useRef<TArgs>();

  // Keep an abort controller for the most recently started request. All
  // previous requests will be aborted before the next is started, so we only
  // need to track the most recent one.
  const abortControllerRef = useRef<AbortController>();

  // We keep a ref to the current set of callbacks being passed into the hook
  // in order to avoid capturing the original callbacks in the closure. This
  // allows callbacks to refer to values from other hooks and behave the way
  // we'd expect them to.
  const callbackRef = useRef(options);

  useEffect(() => {
    callbackRef.current = options;
  }, [options]);

  const callback = useCallback(
    // The main logic for running a query, setting the hook's return values,
    // and calling callbacks.
    //
    // We need to pass signal in rather than using the ref because each promise
    // needs its own signal in order to be aborted without affecting the
    // current query.
    async (args: TArgs, signal: AbortSignal) => {
      try {
        if (callbackRef.current?.onStart) {
          callbackRef.current?.onStart();
        }
        const result = await queryFn(args);
        if (signal.aborted) {
          return;
        }
        if (callbackRef.current?.onSuccess) {
          callbackRef.current?.onSuccess(result);
        }
        statusDispatch({ type: 'setStatus', status: successStatus(result) });
      } catch (e) {
        if (callbackRef.current?.onError) {
          callbackRef.current?.onError(e);
        }
        statusDispatch({ type: 'setStatus', status: errorStatus(e) });
      } finally {
        if (callbackRef.current?.onFinished) {
          callbackRef.current?.onFinished();
        }
      }
    },
    [queryFn, callbackRef]
  );

  const startQuery = useCallback(() => {
    if (options?.enabled === false) {
      return;
    }

    statusDispatch({ type: 'setStatus', status: pendingStatus() });
    if (abortControllerRef.current) {
      // Abort the previous request if it's still running. This doesn't abort
      // the promise itself, but it does prevent the promise from updating
      // the state.
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    callback(queryArgs, abortController.signal);
  }, [callback, queryArgs, options?.enabled]);

  useEffect(() => {
    // We want to start a new request if the arguments change. useEffect
    // nominally takes care of this, but it does reference comparison, making
    // the detection more sensitive than we want. To make sure we're only
    // starting a query when the args have actually changed, we use `isEqual`
    // from lodash to do a deep comparison.

    if (options?.enabled === false) {
      return;
    }

    if (!isEqual(queryArgs, lastArgsRef.current)) {
      lastArgsRef.current = queryArgs;
      startQuery();
    }
  }, [startQuery, queryArgs, options?.enabled]);

  return { ...status, reload: startQuery };
}

/** Combines multiple objects containing query lifecycle callbacks. */
export function composeCallbacks<TResult>(
  callbacks: FBQueryOptions<TResult>[]
): FBQueryOptions<TResult> {
  return {
    onStart: () => {
      for (const c of callbacks) {
        if (c.onStart) {
          c.onStart();
        }
      }
    },
    onSuccess: result => {
      for (const c of callbacks) {
        if (c.onSuccess) {
          c.onSuccess(result);
        }
      }
    },
    onError: e => {
      for (const c of callbacks) {
        if (c.onError) {
          c.onError(e);
        }
      }
    },
    onFinished: () => {
      for (const c of callbacks) {
        if (c.onFinished) {
          c.onFinished();
        }
      }
    },
  };
}
